import {
  Coins,
  MoreHorizontal,
  PiggyBank,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuditTrailPanel } from '@/components/shared/audit-trail-panel';
import { AvatarUploader } from '@/components/shared/avatar-uploader';
import { ChartCard } from '@/components/shared/chart-card';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { CountrySelect } from '@/components/shared/country-select';
import { CurrencySelect } from '@/components/shared/currency-select';
import { DataTable } from '@/components/shared/data-table';
import {
  DateRangeFilter,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FileDropzone } from '@/components/shared/file-dropzone';
import { FilterBar } from '@/components/shared/filter-bar';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { MoneyDisplay } from '@/components/shared/money-display';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { PlanUpgradePrompt } from '@/components/shared/plan-upgrade-prompt';
import { ProgressCard } from '@/components/shared/progress-card';
import { QrPanel } from '@/components/shared/qr-panel';
import { RichTextEditor } from '@/components/shared/rich-text-editor';
import { SearchInput } from '@/components/shared/search-input';
import { ShareDialog } from '@/components/shared/share-dialog';
import { StatCard } from '@/components/shared/stat-card';
import {
  EmptyState,
  ErrorState,
  PermissionDeniedState,
} from '@/components/shared/states';
import { Tip } from '@/components/shared/tip';
import { Timeline } from '@/components/shared/timeline';
import { TimezoneSelect } from '@/components/shared/timezone-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { MultiSelectListbox } from '@/components/ui/multi-select-listbox';
import { PhoneInput } from '@/components/ui/phone-input';
import { ProgressBar } from '@/components/ui/progress-bar';
import { RadioGroup, RadioGroupOption } from '@/components/ui/radio-group';
import { Stepper } from '@/components/ui/stepper';
import { SwitchField } from '@/components/ui/switch';
import { TagInput } from '@/components/ui/tag-input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { Tooltip } from '@/components/ui/tooltip';
import { TruncatedText } from '@/components/ui/truncated-text';
import { usePreferences } from '@/stores/preferences-store';

import { ApiStatus } from './api-status';
import { GallerySection, StateRow } from './gallery-section';
import { UnityLogoAnimation } from './unity-logo-animation';
import { ThemeToggle } from './theme-toggle';

/**
 * Every widget, in every state, in both themes.
 *
 * This page is a Phase 0 deliverable and stays current for the life of the product:
 * a new widget is not finished until it appears here. It is where a dropdown that
 * clips inside a dialog, a control that vanishes in dark mode, or a disabled state
 * nobody styled gets caught — before a church finds it.
 */
export const WidgetGalleryPage = () => {
  const { t } = useTranslation(['gallery', 'common']);
  const timezone = usePreferences((state) => state.timezone);

  const [text, setText] = useState('');
  const [email, setEmail] = useState('not-an-email');
  const [notes, setNotes] = useState('');
  const [phone, setPhone] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>(['choir', 'usher']);
  const [fund, setFund] = useState<string | null>('tithe');
  const [departments, setDepartments] = useState<string[]>(['choir']);
  const [member, setMember] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [country, setCountry] = useState<'GH' | null>('GH');
  const [currency, setCurrency] = useState<string | null>('GHS');
  const [zone, setZone] = useState<string | null>(timezone);
  const [date, setDate] = useState<string | null>(null);
  const [instant, setInstant] = useState<string | null>(null);
  const [range, setRange] = useState<DateRange>({
    preset: 'thisMonth',
    from: null,
    to: null,
  });
  const [amount, setAmount] = useState<number | null>(25_000);
  const [smsReceipt, setSmsReceipt] = useState(true);
  const [anonymous, setAnonymous] = useState(false);
  const [frequency, setFrequency] = useState('monthly');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(2);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<Set<string>>(new Set(['c2']));
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'date',
    direction: 'desc',
  });
  const [story, setStory] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [dialogFund, setDialogFund] = useState<string | null>(null);
  const [dialogDate, setDialogDate] = useState<string | null>(null);
  const [dialogAmount, setDialogAmount] = useState<number | null>(null);

  const fundOptions = [
    { value: 'tithe', label: t('funds.tithe'), group: t('funds.groupGeneral') },
    { value: 'offering', label: t('funds.offering'), group: t('funds.groupGeneral') },
    { value: 'welfare', label: t('funds.welfare'), group: t('funds.groupGeneral') },
    { value: 'building', label: t('funds.building'), group: t('funds.groupProjects') },
    { value: 'missions', label: t('funds.missions'), group: t('funds.groupProjects') },
    {
      value: 'camp',
      label: t('funds.closed'),
      group: t('funds.groupProjects'),
      disabled: true,
    },
  ];

  const departmentOptions = [
    { value: 'choir', label: t('departments.choir') },
    { value: 'ushers', label: t('departments.ushers') },
    { value: 'media', label: t('departments.media') },
    { value: 'children', label: t('departments.children') },
    { value: 'prayer', label: t('departments.prayer') },
  ];

  const memberResults = [
    { id: 'm1', label: t('members.ama'), description: '+233 24 412 3456' },
    { id: 'm2', label: t('members.kofi'), description: '+233 20 998 1122' },
    { id: 'm3', label: t('members.esi'), description: '+233 55 300 7788' },
  ].filter((entry) => entry.label.toLowerCase().includes(memberQuery.toLowerCase()));

  const rows = [
    { id: 'c1', member: t('members.ama'), fund: t('funds.tithe'), method: t('table.cash'), date: '2026-08-02', amount: 25_000 },
    { id: 'c2', member: t('members.kofi'), fund: t('funds.building'), method: t('table.mobileMoney'), date: '2026-08-02', amount: 150_000 },
    { id: 'c3', member: t('members.esi'), fund: t('funds.offering'), method: t('table.bank'), date: '2026-08-01', amount: 8_050 },
  ];

  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <PageHeader
          title={t('title')}
          description={t('description')}
          meta={<ApiStatus />}
          headerActions={<ThemeToggle />}
        />

        <div className="space-y-10">
          <GallerySection title={t('section.animations')}>
            <StateRow label={t('animations.unityLogo')}>
              <div className="flex w-full flex-col items-center gap-3 py-4">
                <UnityLogoAnimation className="size-24" />
                <p className="max-w-sm text-center text-body-sm text-fg-secondary">
                  {t('animations.unityLogoDescription')}
                </p>
              </div>
            </StateRow>
          </GallerySection>

          <GallerySection title={t('section.buttons')}>
            <StateRow label={t('state.default')}>
              <Button>{t('buttons.primary')}</Button>
              <Button variant="secondary">{t('buttons.secondary')}</Button>
              <Button variant="outline">{t('buttons.outline')}</Button>
              <Button variant="ghost">{t('buttons.ghost')}</Button>
              <Button variant="destructive">{t('buttons.destructive')}</Button>
              <Button variant="link">{t('buttons.link')}</Button>
            </StateRow>

            <StateRow label={t('state.loading')}>
              <Button loading>{t('buttons.primary')}</Button>
              <Button variant="outline" loading>
                {t('buttons.outline')}
              </Button>
              <Button variant="destructive" loading>
                {t('buttons.destructive')}
              </Button>
            </StateRow>

            <StateRow label={t('state.disabled')}>
              <Button disabled>{t('buttons.primary')}</Button>
              <Button variant="secondary" disabled>
                {t('buttons.secondary')}
              </Button>
              <Button variant="destructive" disabled>
                {t('buttons.destructive')}
              </Button>
            </StateRow>

            <StateRow label="sm / md / lg / kiosk">
              <Button size="sm">{t('buttons.primary')}</Button>
              <Button size="md">{t('buttons.primary')}</Button>
              <Button size="lg">{t('buttons.primary')}</Button>
              <Button size="kiosk" leadingIcon={<UserPlus className="h-6 w-6" />}>
                {t('buttons.kiosk')}
              </Button>
            </StateRow>
          </GallerySection>

          <GallerySection title={t('section.inputs')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label={t('inputs.nameLabel')}
                tipId="gallery.name"
                required
                requiredLabel={t('common:form.required')}
              >
                {(field) => (
                  <Input
                    {...field}
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    placeholder={t('inputs.namePlaceholder')}
                  />
                )}
              </FormField>

              <FormField label={t('inputs.emailLabel')} error={t('inputs.emailError')}>
                {(field) => (
                  <Input
                    {...field}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </FormField>

              <FormField label={t('inputs.phoneLabel')}>
                {(field) => (
                  <PhoneInput
                    {...field}
                    value={phone}
                    onChange={setPhone}
                    searchPlaceholder={t('selection.searchPlaceholder')}
                    emptyMessage={t('selection.noMatches')}
                  />
                )}
              </FormField>

              <FormField label={t('inputs.tagsLabel')}>
                {(field) => (
                  <TagInput
                    {...field}
                    value={tags}
                    onChange={setTags}
                    placeholder={t('inputs.tagsPlaceholder')}
                    removeLabel={t('selection.remove')}
                  />
                )}
              </FormField>

              <FormField
                label={t('inputs.notesLabel')}
                className="sm:col-span-2"
                optionalLabel={t('common:field.optional')}
              >
                {(field) => (
                  <Textarea
                    {...field}
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder={t('inputs.notesPlaceholder')}
                    maxLength={280}
                    showCount
                    autoGrow
                  />
                )}
              </FormField>
            </div>

            <StateRow label={t('state.disabled')}>
              <Input disabled value={t('inputs.namePlaceholder')} readOnly />
              <Textarea disabled value={t('inputs.notesPlaceholder')} readOnly />
            </StateRow>
          </GallerySection>

          <GallerySection title={t('section.selection')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label={t('selection.fundLabel')}>
                {(field) => (
                  <Listbox
                    {...field}
                    value={fund}
                    onChange={setFund}
                    options={fundOptions}
                    searchable
                    clearable
                    placeholder={t('selection.fundPlaceholder')}
                    searchPlaceholder={t('selection.searchPlaceholder')}
                    emptyMessage={t('selection.noMatches')}
                    clearLabel={t('selection.clear')}
                  />
                )}
              </FormField>

              <FormField label={t('selection.departmentsLabel')}>
                {(field) => (
                  <MultiSelectListbox
                    {...field}
                    value={departments}
                    onChange={setDepartments}
                    options={departmentOptions}
                    placeholder={t('selection.departmentsPlaceholder')}
                    searchPlaceholder={t('selection.searchPlaceholder')}
                    emptyMessage={t('selection.noMatches')}
                    removeLabel={t('selection.remove')}
                    selectAllLabel={t('selection.selectAll')}
                    clearAllLabel={t('selection.clearAll')}
                    summaryLabel={(count) => t('selection.selectedCount', { count })}
                  />
                )}
              </FormField>

              <FormField label={t('selection.memberLabel')}>
                {(field) => (
                  <EntityCombobox
                    {...field}
                    value={member}
                    onChange={setMember}
                    options={memberResults}
                    onQueryChange={setMemberQuery}
                    placeholder={t('selection.memberPlaceholder')}
                    emptyMessage={t('selection.noMatches')}
                    clearLabel={t('selection.clear')}
                    createAction={{
                      label: t('selection.createMember'),
                      onClick: () => toast.info(t('selection.createMember')),
                    }}
                  />
                )}
              </FormField>

              <FormField label={t('selection.countryLabel')}>
                {(field) => (
                  <CountrySelect
                    {...field}
                    value={country}
                    onChange={(next) => setCountry(next as 'GH' | null)}
                    showDialCode
                    searchPlaceholder={t('selection.searchPlaceholder')}
                    emptyMessage={t('selection.noMatches')}
                  />
                )}
              </FormField>

              <FormField label={t('selection.currencyLabel')}>
                {(field) => (
                  <CurrencySelect
                    {...field}
                    value={currency}
                    onChange={setCurrency}
                    currencies={['GHS', 'USD', 'GBP', 'EUR', 'NGN']}
                    searchPlaceholder={t('selection.searchPlaceholder')}
                    emptyMessage={t('selection.noMatches')}
                  />
                )}
              </FormField>

              <FormField label={t('selection.timezoneLabel')}>
                {(field) => (
                  <TimezoneSelect
                    {...field}
                    value={zone}
                    onChange={setZone}
                    searchPlaceholder={t('selection.searchPlaceholder')}
                    emptyMessage={t('selection.noMatches')}
                  />
                )}
              </FormField>
            </div>

            <StateRow label={`${t('state.disabled')} / ${t('state.error')}`}>
              <div className="w-56">
                <Listbox
                  value="tithe"
                  onChange={() => {}}
                  options={fundOptions}
                  disabled
                />
              </div>
              <div className="w-56">
                <Listbox
                  value={null}
                  onChange={() => {}}
                  options={fundOptions}
                  invalid
                  placeholder={t('selection.fundPlaceholder')}
                />
              </div>
            </StateRow>
          </GallerySection>

          <GallerySection title={t('section.dateTime')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label={t('dateTime.contributionDate')}>
                {(field) => (
                  <DatePicker
                    {...field}
                    value={date}
                    onChange={setDate}
                    clearable
                    clearLabel={t('selection.clear')}
                    placeholder={t('dateTime.datePlaceholder')}
                    calendarLabels={{
                      month: t('dateTime.month'),
                      year: t('dateTime.year'),
                      previousMonth: t('dateTime.previousMonth'),
                      nextMonth: t('dateTime.nextMonth'),
                    }}
                  />
                )}
              </FormField>

              <FormField label={t('dateTime.serviceStart')}>
                {(field) => (
                  <DateTimePicker
                    id={field.id}
                    value={instant}
                    onChange={setInstant}
                    datePlaceholder={t('dateTime.datePlaceholder')}
                    timezoneNotice={(tz) => t('dateTime.timeInZone', { timezone: tz })}
                    labels={{
                      hour: t('dateTime.hour'),
                      minute: t('dateTime.minute'),
                      meridiem: t('dateTime.meridiem'),
                      month: t('dateTime.month'),
                      year: t('dateTime.year'),
                      previousMonth: t('dateTime.previousMonth'),
                      nextMonth: t('dateTime.nextMonth'),
                    }}
                  />
                )}
              </FormField>
            </div>

            <StateRow label={t('dateTime.rangeLabel')}>
              <DateRangeFilter
                value={range}
                onChange={setRange}
                labels={{
                  today: t('common:date.today'),
                  yesterday: t('common:date.yesterday'),
                  thisWeek: t('common:date.thisWeek'),
                  lastWeek: t('common:date.lastWeek'),
                  thisMonth: t('common:date.thisMonth'),
                  lastMonth: t('common:date.lastMonth'),
                  thisQuarter: t('common:date.thisQuarter'),
                  thisYear: t('common:date.thisYear'),
                  lastYear: t('common:date.lastYear'),
                  allTime: t('common:date.allTime'),
                  custom: t('common:date.custom'),
                  from: t('common:field.startDate'),
                  to: t('common:field.endDate'),
                }}
              />
            </StateRow>
          </GallerySection>

          <GallerySection title={t('section.toggles')}>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-4">
                <SwitchField
                  id="sms-receipt"
                  label={t('toggles.smsLabel')}
                  description={t('toggles.smsDescription')}
                  checked={smsReceipt}
                  onCheckedChange={setSmsReceipt}
                />
                <div className="flex items-center gap-2.5">
                  <Checkbox
                    id="anonymous"
                    checked={anonymous}
                    onCheckedChange={(checked) => setAnonymous(checked === true)}
                  />
                  <label htmlFor="anonymous" className="cursor-pointer text-body text-fg">
                    {t('toggles.anonymousLabel')}
                  </label>
                </div>
                <div className="flex items-center gap-2.5">
                  <Checkbox id="indeterminate" checked="indeterminate" />
                  <label htmlFor="indeterminate" className="text-body text-fg-secondary">
                    {t('state.selected')}
                  </label>
                </div>
              </div>

              <fieldset>
                <legend className="mb-2 text-body-sm font-medium text-fg">
                  {t('toggles.frequencyLabel')}
                </legend>
                <RadioGroup value={frequency} onValueChange={setFrequency}>
                  <RadioGroupOption id="freq-weekly" value="weekly" label={t('toggles.weekly')} />
                  <RadioGroupOption id="freq-monthly" value="monthly" label={t('toggles.monthly')} />
                  <RadioGroupOption
                    id="freq-once"
                    value="once"
                    label={t('toggles.onceLabel')}
                    description={t('toggles.onceDescription')}
                  />
                </RadioGroup>
              </fieldset>
            </div>
          </GallerySection>

          <GallerySection title={t('section.money')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label={t('money.amountLabel')}>
                {(field) => (
                  <MoneyInput
                    id={field.id}
                    aria-describedby={field['aria-describedby']}
                    value={amount}
                    onChange={setAmount}
                  />
                )}
              </FormField>

              <FormField label={t('selection.currencyLabel')}>
                {(field) => (
                  <MoneyInput
                    id={field.id}
                    aria-describedby={field['aria-describedby']}
                    value={amount}
                    onChange={setAmount}
                    currency={currency ?? 'GHS'}
                    onCurrencyChange={setCurrency}
                    allowedCurrencies={['GHS', 'USD', 'GBP']}
                  />
                )}
              </FormField>
            </div>

            <StateRow label={t('state.default')}>
              <span className="text-fg-secondary">{t('money.positive')}:</span>
              <MoneyDisplay amount={125_000} currency="GHS" colorBySign />
              <span className="text-fg-secondary">{t('money.negative')}:</span>
              <MoneyDisplay amount={-4_500} currency="GHS" colorBySign />
              <span className="text-fg-secondary">{t('money.large')}:</span>
              <MoneyDisplay amount={48_950_000} currency="GHS" size="lg" />
            </StateRow>

            <ProgressCard
              title={t('money.targetLabel')}
              description={t('money.targetDescription')}
              raised={4_820_000}
              target={7_500_000}
              currency="GHS"
              progressLabel={t('money.progressLabel')}
              summary={(parts) => t('money.raisedSummary', parts)}
            />
          </GallerySection>

          <GallerySection title={t('section.feedback')}>
            <StateRow label={t('feedback.badges')}>
              <Badge>{t('common:status.draft')}</Badge>
              <Badge variant="brand">{t('common:status.scheduled')}</Badge>
              <Badge variant="success">{t('common:status.paid')}</Badge>
              <Badge variant="warning">{t('common:status.pending')}</Badge>
              <Badge variant="danger">{t('common:status.overdue')}</Badge>
              <Badge variant="info">{t('common:status.sent')}</Badge>
            </StateRow>

            <StateRow label={t('feedback.progress')}>
              <div className="w-64">
                <ProgressBar value={64} label={t('feedback.pledgeProgress')} valueText="64%" />
              </div>
              <div className="w-64">
                <ProgressBar
                  value={112}
                  label={t('feedback.pledgeProgress')}
                  valueText="112%"
                />
              </div>
            </StateRow>

            <StateRow label={t('feedback.steps')}>
              <Stepper
                label={t('feedback.stepperLabel')}
                current={1}
                className="w-full max-w-2xl"
                steps={[
                  { id: 'amount', label: t('feedback.stepAmount') },
                  { id: 'details', label: t('feedback.stepDetails') },
                  { id: 'confirm', label: t('feedback.stepConfirm') },
                  { id: 'receipt', label: t('feedback.stepReceipt') },
                ]}
              />
            </StateRow>

            <StateRow label="TruncatedText">
              <div className="w-72">
                <TruncatedText text={t('feedback.truncated')} />
              </div>
            </StateRow>
          </GallerySection>

          <GallerySection title={t('section.overlays')}>
            <StateRow label={t('state.default')}>
              <Button onClick={() => setFormOpen(true)}>{t('overlays.openForm')}</Button>
              <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
                {t('overlays.openConfirm')}
              </Button>
              <Button variant="outline" onClick={() => setShareOpen(true)}>
                {t('overlays.openShare')}
              </Button>
              <Button
                variant="secondary"
                onClick={() =>
                  toast.success(t('overlays.toastTitle'), t('overlays.toastBody'))
                }
              >
                {t('overlays.showToast')}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={t('overlays.menu')}>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>{t('overlays.menuEdit')}</DropdownMenuItem>
                  <DropdownMenuItem>{t('overlays.menuDuplicate')}</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem destructive>{t('overlays.menuDelete')}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Tooltip content={t('overlays.tooltip')}>
                <Button variant="ghost">Tooltip</Button>
              </Tooltip>
            </StateRow>

            <StateRow label={t('tips.modes')}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-8">
                <div className="space-y-1">
                  <p className="text-caption text-fg-tertiary">{t('tips.inline')}</p>
                  <Tip tipId="gallery.name" mode="inline" />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-body-sm text-fg">{t('tips.iconLabel')}</span>
                  <Tip tipId="gallery.iconDemo" mode="icon" />
                </div>
                <Tip tipId="gallery.givingStat" mode="label">
                  {t('stats.givingThisMonth')}
                </Tip>
              </div>
            </StateRow>
          </GallerySection>

          <GallerySection title={t('section.dataDisplay')}>
            <div className="grid gap-4 sm:grid-cols-3">
              <StatCard
                label={t('stats.givingThisMonth')}
                money={{ amount: 4_820_000, currency: 'GHS' }}
                icon={<Coins className="size-5" />}
                tipId="gallery.givingStat"
                comparison={{
                  change: 0.12,
                  formatted: '+12%',
                  label: t('stats.againstLastMonth'),
                }}
              />
              <StatCard
                label={t('stats.newMembers')}
                value="38"
                icon={<Users className="size-5" />}
                comparison={{
                  change: -0.04,
                  formatted: '-4%',
                  label: t('stats.againstLastMonth'),
                }}
              />
              <StatCard
                label={t('stats.attendance')}
                icon={<PiggyBank className="size-5" />}
                loading
              />
            </div>

            <FilterBar
              appliedCount={2}
              onClearAll={() => setSearch('')}
              labels={{
                filters: t('filters.label'),
                clearAll: t('filters.clearAll'),
                applied: (count) => t('filters.applied', { count }),
              }}
              leading={
                <SearchInput
                  value={search}
                  onChange={setSearch}
                  placeholder={t('filters.searchPlaceholder')}
                  clearLabel={t('selection.clear')}
                />
              }
            >
              {({ popoverPortal }) => (
                <>
                  <div className="w-52">
                    <Listbox
                      value={fund}
                      onChange={setFund}
                      options={fundOptions}
                      clearable
                      clearLabel={t('selection.clear')}
                      placeholder={t('selection.fundPlaceholder')}
                      popoverPortal={popoverPortal}
                    />
                  </div>
                  <DateRangeFilter
                    value={range}
                    onChange={setRange}
                    popoverPortal={popoverPortal}
                    labels={{
                      today: t('common:date.today'),
                      yesterday: t('common:date.yesterday'),
                      thisWeek: t('common:date.thisWeek'),
                      lastWeek: t('common:date.lastWeek'),
                      thisMonth: t('common:date.thisMonth'),
                      lastMonth: t('common:date.lastMonth'),
                      thisQuarter: t('common:date.thisQuarter'),
                      thisYear: t('common:date.thisYear'),
                      lastYear: t('common:date.lastYear'),
                      allTime: t('common:date.allTime'),
                      custom: t('common:date.custom'),
                      from: t('common:field.startDate'),
                      to: t('common:field.endDate'),
                    }}
                  />
                </>
              )}
            </FilterBar>

            <DataTable
              rows={rows}
              rowKey={(row) => row.id}
              actionsHeader={t('common:table.actions')}
              totalsLabel={t('common:table.total')}
              totals={{ amount: { amount: 183_050, currency: 'GHS' } }}
              sort={{
                field: sort.field,
                direction: sort.direction,
                onChange: (field, direction) => setSort({ field, direction }),
              }}
              selection={{
                selected,
                onChange: setSelected,
                selectAllLabel: t('common:table.actions'),
                selectRowLabel: t('common:table.actions'),
              }}
              columns={[
                { id: 'date', header: t('table.date'), cell: (row) => row.date, sortable: true },
                { id: 'member', header: t('table.member'), cell: (row) => row.member, sortable: true },
                { id: 'fund', header: t('table.fund'), cell: (row) => row.fund },
                {
                  id: 'method',
                  header: t('table.method'),
                  cell: (row) => <Badge>{row.method}</Badge>,
                  hideBelowMd: true,
                },
                {
                  id: 'amount',
                  header: t('table.amount'),
                  money: (row) => ({ amount: row.amount, currency: 'GHS' }),
                  sortable: true,
                },
              ]}
              rowActions={() => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label={t('overlays.menu')}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>{t('overlays.menuEdit')}</DropdownMenuItem>
                    <DropdownMenuItem destructive>{t('overlays.menuDelete')}</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            />

            <Pagination
              page={page}
              pageSize={pageSize}
              totalItems={214}
              totalPages={9}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              labels={{
                showing: (parts) => t('pagination.showing', parts),
                perPage: t('pagination.perPage'),
                first: t('pagination.first'),
                previous: t('pagination.previous'),
                next: t('pagination.next'),
                last: t('pagination.last'),
              }}
            />

            <div className="grid gap-4 lg:grid-cols-2">
              <ChartCard
                title={t('stats.chartTitle')}
                description={t('stats.chartDescription')}
                hasData={false}
                empty={{
                  title: t('pageStates.emptyTitle'),
                  body: t('pageStates.emptyBody'),
                }}
              >
                <div />
              </ChartCard>

              <AuditTrailPanel
                title={t('timeline.auditTitle')}
                emptyTitle={t('timeline.auditEmpty')}
                emptyBody={t('timeline.auditEmptyBody')}
                emptyValueLabel={t('timeline.notSet')}
                entries={[
                  {
                    id: 'a1',
                    at: '2026-08-02T09:14:00.000Z',
                    summary: t('timeline.auditSummary'),
                    actor: t('timeline.by'),
                    changes: [
                      {
                        field: t('timeline.fieldFund'),
                        before: t('funds.offering'),
                        after: t('funds.building'),
                      },
                      { field: t('timeline.fieldAmount'), before: null, after: 'GHS 250.00' },
                    ],
                  },
                ]}
              />
            </div>

            <Timeline
              entries={[
                {
                  id: 't1',
                  at: '2026-08-03T08:30:00.000Z',
                  title: t('timeline.created'),
                  actor: t('timeline.by'),
                  tone: 'success',
                  icon: <Coins className="h-4 w-4" />,
                },
                {
                  id: 't2',
                  at: '2026-07-28T15:02:00.000Z',
                  title: t('timeline.updated'),
                  actor: t('timeline.by'),
                  tone: 'info',
                },
                {
                  id: 't3',
                  at: '2026-06-11T11:45:00.000Z',
                  title: t('timeline.reversed'),
                  actor: t('timeline.by'),
                  tone: 'danger',
                },
              ]}
            />
          </GallerySection>

          <GallerySection title={t('section.pageStates')}>
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="rounded-lg border border-border bg-surface">
                <EmptyState
                  title={t('pageStates.emptyTitle')}
                  body={t('pageStates.emptyBody')}
                  action={<Button size="sm">{t('pageStates.emptyAction')}</Button>}
                />
              </div>
              <div className="rounded-lg border border-border bg-surface">
                <ErrorState
                  title={t('pageStates.errorTitle')}
                  body={t('pageStates.errorBody')}
                  onRetry={() => {}}
                  retryLabel={t('common:action.retry')}
                  requestId="018f3c2a-9a1e-7c3b-9c2e-4f1b7d5a2e10"
                />
              </div>
              <div className="rounded-lg border border-border bg-surface">
                <PermissionDeniedState
                  title={t('pageStates.deniedTitle')}
                  body={t('pageStates.deniedBody')}
                />
              </div>
            </div>

            <PlanUpgradePrompt
              title={t('stats.chartTitle')}
              description={t('stats.chartDescription')}
              planNote={t('common:nav.billing')}
              action={{ label: t('common:action.viewDetails'), onClick: () => {} }}
            />
          </GallerySection>

          <GallerySection title={t('section.media')}>
            <div className="grid gap-6 lg:grid-cols-2">
              <FileDropzone
                onFiles={() => {}}
                accept="application/pdf,image/png,image/jpeg"
                acceptLabel={t('media.accepts')}
                maxSizeLabel={t('media.maxSize')}
                labels={{ prompt: t('media.dropPrompt'), remove: t('media.removeFile') }}
                files={[{ name: 'august-budget.pdf', size: '412 KB' }]}
                onRemove={() => {}}
              />

              <div className="space-y-6">
                <AvatarUploader
                  name={t('members.ama')}
                  onUpload={() => {}}
                  labels={{
                    change: t('media.avatarChange'),
                    remove: t('media.avatarRemove'),
                    cropTitle: t('media.cropTitle'),
                    zoom: t('media.cropZoom'),
                    apply: t('media.cropApply'),
                    cancel: t('media.cropCancel'),
                    instructions: t('media.cropInstructions'),
                    closeCropper: t('common:action.close'),
                  }}
                />

                <QrPanel
                  value="https://trinity.church/give/grace-chapel"
                  label={t('media.qrLabel')}
                  caption={t('media.qrCaption')}
                  downloadLabel={t('media.qrDownload')}
                  downloadName="giving-link"
                  size={160}
                />
              </div>
            </div>

            <RichTextEditor
              value={story}
              onChange={setStory}
              placeholder={t('media.editorPlaceholder')}
              labels={{
                toolbar: t('media.editorToolbar'),
                bold: t('media.bold'),
                italic: t('media.italic'),
                heading2: t('media.heading2'),
                heading3: t('media.heading3'),
                bulletList: t('media.bulletList'),
                orderedList: t('media.orderedList'),
                quote: t('media.quote'),
                link: t('media.link'),
                image: t('media.image'),
                undo: t('media.undo'),
                redo: t('media.redo'),
              }}
            />
          </GallerySection>
        </div>
      </div>

      {/* The dialog that proves the portal pattern: a Listbox, a DatePicker and a
          MoneyInput inside a scrolling body, all opening within the dialog. */}
      <FormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        title={t('overlays.formTitle')}
        description={t('overlays.formDescription')}
        submitLabel={t('common:action.save')}
        cancelLabel={t('common:action.cancel')}
        closeLabel={t('common:action.close')}
        onSubmit={() => {
          setFormOpen(false);
          toast.success(t('overlays.toastTitle'), t('overlays.toastBody'));
        }}
      >
        <FormField label={t('selection.memberLabel')}>
          {(field) => (
            <EntityCombobox
              {...field}
              value={member}
              onChange={setMember}
              options={memberResults}
              onQueryChange={setMemberQuery}
              placeholder={t('selection.memberPlaceholder')}
              emptyMessage={t('selection.noMatches')}
              clearLabel={t('selection.clear')}
              popoverPortal="dialog"
            />
          )}
        </FormField>

        <FormField label={t('selection.fundLabel')} required>
          {(field) => (
            <Listbox
              {...field}
              value={dialogFund}
              onChange={setDialogFund}
              options={fundOptions}
              searchable
              placeholder={t('selection.fundPlaceholder')}
              searchPlaceholder={t('selection.searchPlaceholder')}
              emptyMessage={t('selection.noMatches')}
              popoverPortal="dialog"
            />
          )}
        </FormField>

        <FormField label={t('money.amountLabel')} required>
          {(field) => (
            <MoneyInput
              id={field.id}
              aria-describedby={field['aria-describedby']}
              value={dialogAmount}
              onChange={setDialogAmount}
              popoverPortal="dialog"
            />
          )}
        </FormField>

        <FormField label={t('dateTime.contributionDate')} required>
          {(field) => (
            <DatePicker
              {...field}
              value={dialogDate}
              onChange={setDialogDate}
              placeholder={t('dateTime.datePlaceholder')}
              popoverPortal="dialog"
              calendarLabels={{
                month: t('dateTime.month'),
                year: t('dateTime.year'),
                previousMonth: t('dateTime.previousMonth'),
                nextMonth: t('dateTime.nextMonth'),
              }}
            />
          )}
        </FormField>

        <FormField label={t('inputs.notesLabel')}>
          {(field) => (
            <Textarea {...field} value={notes} onChange={(event) => setNotes(event.target.value)} />
          )}
        </FormField>

        {/* Deliberately last, so its menu has to open upward. */}
        <FormField label={t('selection.departmentsLabel')}>
          {(field) => (
            <MultiSelectListbox
              {...field}
              value={departments}
              onChange={setDepartments}
              options={departmentOptions}
              placeholder={t('selection.departmentsPlaceholder')}
              searchPlaceholder={t('selection.searchPlaceholder')}
              emptyMessage={t('selection.noMatches')}
              removeLabel={t('selection.remove')}
              popoverPortal="dialog"
            />
          )}
        </FormField>
      </FormDialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('overlays.confirmTitle')}
        description={t('overlays.confirmDescription')}
        confirmLabel={t('common:action.delete')}
        cancelLabel={t('common:action.cancel')}
        closeLabel={t('common:action.close')}
        destructive
        typeToConfirm={{ value: t('funds.building'), label: t('overlays.confirmTyped') }}
        onConfirm={() => setConfirmOpen(false)}
      />

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        url="https://trinity.church/give/grace-chapel"
        title={t('overlays.shareTitle')}
        description={t('overlays.shareDescription')}
        downloadName="giving-link"
        labels={{
          copy: t('common:action.copy'),
          copied: t('common:action.copied'),
          share: t('common:action.share'),
          download: t('media.qrDownload'),
          close: t('common:action.close'),
          qrLabel: t('overlays.shareQr'),
        }}
      />
    </div>
  );
};
